import { useQuery } from '@apollo/client/react';
import * as queryString from '../../../Utils/queryStringHelper';
import React, { type FC, useLayoutEffect } from 'react';
import { type RouteComponentProps, withRouter } from '../../../Utils/withRouter';
import { Navigate } from 'react-router-dom';
import { QUERY_GET_USER_BY_MAIL } from '../../../graphql/Authentication/query';
import { ERouteOption } from '../../../Interface/Dashboard/CategoryInterface';
import { type UserInterface } from '../../../Interface/UserInterface';
import AuthService from '../../../Services/AuthService';
import { toString } from '../../../Services/Calcul';
import { createLoginUrlWithCallback } from '../../../Utils/createLoginUrlWithCallback';
import { redirectToTunnel } from '../../../Utils/encryption';
import MainLoadingDigidom from '../../Loading/MainLoadingDigidom';

// to account : https://app.digidom.pro/?org=ID_ORGANIZATION&mailParam=account/info
// to forward address : https://app.digidom.pro/?org=ID_ORGANIZATION&mailParam=account/forward-address
// to mandat : https://app.digidom.pro/?org=ID_ORGANIZATION&mailParam=account/mandate
// to letter: https://app.digidom.pro/?org=ID_ORGANIZATION&mailParam=letter
// to invoices: https://app.digidom.pro/?org=ID_ORGANIZATION&mailParam=document/invoices
// to documents: https://app.digidom.pro/?org=ID_ORGANIZATION&mailParam=document/documents
// to offer: https://app.digidom.pro/?org=ID_ORGANIZATION&mailParam=account/?expanded=OFFER
// to profil(rib): https://app.digidom.pro/?org=ID_ORGANIZATION&mailParam=account/?expanded=PROFIL

const Impersonation: FC<RouteComponentProps> = (props) => {
  const {
    location: { search },
  } = props;

  const queries = queryString.parse(search.substr(1, search.length));

  const {
    switch_user,
    user: userId,
    token: tokenFromURL,
    org: organizationId,
    edit,
    edit_immat: editImmatStatus,
    mailParam,
    isSwitch,
    to,
    return_link,
  } = queries;

  const token = AuthService.getAccessToken();

  const [isImpersonnation, setIsImpersonnation] = React.useState(false);
  const [isInitialized, setIsInitialized] = React.useState(false);

  // Execute useEffect logic BEFORE conditional checks
  useLayoutEffect(() => {
    if (switch_user) {
      setIsImpersonnation(true);
    }

    // restore skip payment if not edit immat
    if (!editImmatStatus && switch_user) {
      AuthService.setShowBeforePayment('');
    }
    // Set all query string impersonation in local storage
    if (!isSwitch) {
      const editType = edit ? 'DOM' : editImmatStatus ? 'IMMAT' : '';
      AuthService.setSwitchUser(toString(switch_user));
      AuthService.setOrganizationIDByAdmin(toString(organizationId));
      AuthService.setIsEditOrganization(editType);
      AuthService.setToPage(toString(to));
      AuthService.setAdminLink(toString(return_link));
    }

    // If no impersonation, connect with token and userId
    if (tokenFromURL && userId && !switch_user) {
      AuthService.setIsFromBV('');
      AuthService.setAccessToken(tokenFromURL.toString());
      AuthService.setUserId(userId.toString());
      window.history.replaceState(null, '', `/${ERouteOption.LETTER}`);
      window.location.reload();
    }

    setIsInitialized(true);
  }, []);

  // Don't render until useLayoutEffect has completed
  if (!isInitialized) {
    return null;
  }

  // If impersonation
  if (isImpersonnation || isSwitch) {
    return <GetUserBySwitchUser />;
  }

  const loginQuery = createLoginUrlWithCallback();

  if (!token && !tokenFromURL) {
    if (!mailParam) {
      return <Navigate to={`/auth/login?${loginQuery}`} replace />;
    }
    return <Navigate to={`/auth/login/?${loginQuery}`} replace />;
  } else {
    if (mailParam) {
      return <Navigate to={`/${mailParam}`} replace />;
    }

    return <Navigate to={`/${ERouteOption.LETTER}`} replace />;
  }
};

export default withRouter(Impersonation);

const GetUserBySwitchUser: FC = () => {
  const to = AuthService.getToPage();
  const switchUser = AuthService.getSwitchUser();

  const token = AuthService.getAccessToken();

  const { data, loading, error } = useQuery<{ getSwitchUserByMail: UserInterface }>(
    QUERY_GET_USER_BY_MAIL,
    { variables: { mail: switchUser }, skip: !token },
  );

  const loginQuery = createLoginUrlWithCallback();

  if (!token) {
    return <Navigate to={`/auth/login?${loginQuery}`} replace />;
  }

  if (loading) {
    return <MainLoadingDigidom />;
  }

  if (error) {
    AuthService.clearToken();
    return <Navigate to={`/auth/login?${loginQuery}`} replace />;
  }

  if (!data || !data.getSwitchUserByMail) {
    return null;
  }

  const userData = data.getSwitchUserByMail;

  AuthService.setSwitchUserId(userData.id!);

  const edit = AuthService.getIsEditOrganization();

  const orgID = AuthService.getOrganizationIDByAdmin();

  if (edit && orgID) {
    AuthService.setShowBeforePayment('');
    AuthService.setOrganizationIDByAdmin('');
    AuthService.setIsEditOrganization('');
    const goingToType = edit.trim() === 'DOM' ? { edit: 'true' } : { edit_immat: 'true' };

    redirectToTunnel({
      orgId: orgID,
      params: goingToType,
    });
    return null;
  }

  let goTO = to ? to : ERouteOption.LETTER;

  if (to) {
    AuthService.setToPage('');
  }

  if (orgID) {
    goTO = `${orgID}/${goTO}`;
  }

  return <Navigate to={`/${goTO}`} replace />;
};
